import useAuth from 'hooks/useAuth';
import { createContext, useCallback, useContext, useMemo } from 'react';

const PermissionsContext = createContext({
  permissions: [],
  roles: [],
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  hasRole: () => false,
  hasAnyRole: () => false
});

export const PermissionsProvider = ({ children }) => {
  const { user } = useAuth();

  const permissions = useMemo(() => user?.permissions || [], [user]);
  const roles = useMemo(() => user?.roles || [], [user]);

  /* useEffect(() => {
    console.group('🔐 PermissionsContext Debug');
    console.log('User object:', user);
    console.log('Permissions:', permissions);
    console.log('Roles:', roles);
    console.groupEnd();
  }, [user, permissions, roles]); */

  // Verify if the user has a specific permission
  const hasPermission = useCallback(
    (permission) => {
      if (!permission) return true; // No restriction
      return permissions.includes(permission);
    },
    [permissions]
  );

  // Verify if the user has at least one of the permissions
  const hasAnyPermission = useCallback(
    (permissionList = []) => {
      if (!permissionList.length) return true;
      return permissionList.some((permission) => permissions.includes(permission));
    },
    [permissions]
  );

  // Verify if the user has all the permissions
  const hasAllPermissions = useCallback(
    (permissionList = []) => {
      if (!permissionList.length) return true;
      return permissionList.every((permission) => permissions.includes(permission));
    },
    [permissions]
  );

  // Verify if the user has a specific role
  const hasRole = useCallback(
    (role) => {
      if (!role) return true;
      return roles.includes(role);
    },
    [roles]
  );

  // Verify if the user has at least one of the roles
  const hasAnyRole = useCallback(
    (roleList = []) => {
      if (!roleList.length) return true;
      return roleList.some((role) => roles.includes(role));
    },
    [roles]
  );

  const value = useMemo(
    () => ({
      permissions,
      roles,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      hasRole,
      hasAnyRole
    }),
    [permissions, roles, hasPermission, hasAnyPermission, hasAllPermissions, hasRole, hasAnyRole]
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};
